import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const TAX_RATE = 0.05;
const PACKAGING = 15;
const CONVENIENCE = 6;

const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("fundle_cart", []));
  const [table, setTable] = useState(() => load("fundle_table", null));

  useEffect(() => { localStorage.setItem("fundle_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => {
    if (table) localStorage.setItem("fundle_table", JSON.stringify(table));
  }, [table]);

  const lineKey = (item, variant, addons) =>
    `${item.id}|${variant?.name || ""}|${(addons || []).map((a) => a.name).sort().join(",")}`;

  const addItem = (item, variant = null, addons = [], qty = 1) => {
    setItems((prev) => {
      const key = lineKey(item, variant, addons);
      const idx = prev.findIndex((l) => l.key === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [...prev, {
        key, item_id: item.id, restaurant_id: item.restaurant_id,
        restaurant_name: item.restaurant_name, group_id: item.group_id,
        group_name: item.group_name, name: item.name, price: item.price,
        image: item.image, food_type: item.food_type, prep_time: item.prep_time,
        variant, addons: addons || [], qty,
      }];
    });
  };

  const changeQty = (key, delta) => {
    setItems((prev) => prev
      .map((l) => (l.key === key ? { ...l, qty: l.qty + delta } : l))
      .filter((l) => l.qty > 0));
  };

  const removeLine = (key) => setItems((prev) => prev.filter((l) => l.key !== key));
  const clear = () => setItems([]);

  const lineTotal = (l) => {
    let unit = l.price + (l.variant?.price || 0);
    (l.addons || []).forEach((a) => (unit += a.price || 0));
    return unit * l.qty;
  };

  const qtyForItem = (itemId) =>
    items.filter((l) => l.item_id === itemId).reduce((s, l) => s + l.qty, 0);

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((l) => {
      if (!g[l.restaurant_id]) {
        g[l.restaurant_id] = { restaurant_id: l.restaurant_id, restaurant_name: l.restaurant_name, group_name: l.group_name, lines: [], subtotal: 0 };
      }
      g[l.restaurant_id].lines.push(l);
      g[l.restaurant_id].subtotal += lineTotal(l);
    });
    return Object.values(g);
  }, [items]);

  const totals = useMemo(() => {
    const food = grouped.reduce((s, r) => s + r.subtotal, 0);
    const tax = grouped.reduce((s, r) => s + r.subtotal * TAX_RATE, 0);
    const packaging = grouped.length * PACKAGING;
    const convenience = grouped.length ? CONVENIENCE : 0;
    const payable = food + tax + packaging + convenience;
    return {
      food, tax, packaging, convenience, payable,
      count: items.reduce((s, l) => s + l.qty, 0),
      restaurants: grouped.length,
    };
  }, [grouped, items]);

  return (
    <CartContext.Provider value={{
      items, grouped, totals, table, setTable, addItem, changeQty,
      removeLine, clear, lineTotal, qtyForItem,
    }}>
      {children}
    </CartContext.Provider>
  );
}
